import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Target, TrendingUp, Star, Users, Plus, RefreshCw, CheckCircle, Clock, Award,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PerformancePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("kpi");
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [kpis, setKpis] = useState([]);
  const [kpiAssignments, setKpiAssignments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Filters
  const [selectedPeriode, setSelectedPeriode] = useState("2026-Q1");
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  
  // Dialogs
  const [showKpiDialog, setShowKpiDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  
  // Form states
  const [kpiForm, setKpiForm] = useState({ nama: "", deskripsi: "", target: "", satuan: "persen", bobot: 100, department_id: "" });
  const [assignForm, setAssignForm] = useState({ employee_id: "", kpi_id: "", periode: "2026-Q1" });
  const [scoreForm, setScoreForm] = useState({ nilai_aktual: "", catatan: "" });
  const [reviewForm, setReviewForm] = useState({ employee_id: "", periode: "2026-H1", tipe: "manager" });

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (activeTab === "kpi") {
      fetchKpis();
      fetchKpiAssignments();
    } else if (activeTab === "review") {
      fetchReviews();
    }
  }, [activeTab, selectedPeriode, selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/employees`, { headers: { Authorization: `Bearer ${token}` } });
      setEmployees(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/departments`, { headers: { Authorization: `Bearer ${token}` } });
      setDepartments(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchKpis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/kpi`, { headers: { Authorization: `Bearer ${token}` } });
      setKpis(res.data);
    } catch (err) { toast.error("Gagal memuat KPI"); }
    finally { setLoading(false); }
  };

  const fetchKpiAssignments = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      let url = `${API_URL}/api/kpi/assignments?periode=${selectedPeriode}`;
      if (selectedEmployee !== "all") url += `&employee_id=${selectedEmployee}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setKpiAssignments(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      let url = `${API_URL}/api/performance/reviews?periode=${selectedPeriode}`;
      if (selectedEmployee !== "all") url += `&employee_id=${selectedEmployee}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setReviews(res.data);
    } catch (err) { toast.error("Gagal memuat reviews"); }
    finally { setLoading(false); }
  };

  const handleCreateKpi = async () => {
    if (!kpiForm.nama || !kpiForm.target) { toast.error("Nama dan target wajib diisi"); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/kpi`, {
        ...kpiForm,
        target: parseFloat(kpiForm.target),
        bobot: parseFloat(kpiForm.bobot),
        department_id: kpiForm.department_id || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("KPI berhasil dibuat");
      setShowKpiDialog(false);
      setKpiForm({ nama: "", deskripsi: "", target: "", satuan: "persen", bobot: 100, department_id: "" });
      fetchKpis();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal membuat KPI"); }
    finally { setLoading(false); }
  };

  const handleAssignKpi = async () => {
    if (!assignForm.employee_id || !assignForm.kpi_id) { toast.error("Karyawan dan KPI wajib dipilih"); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/kpi/assign`, assignForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("KPI berhasil di-assign");
      setShowAssignDialog(false);
      setAssignForm({ employee_id: "", kpi_id: "", periode: selectedPeriode });
      fetchKpiAssignments();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal assign KPI"); }
    finally { setLoading(false); }
  };

  const handleUpdateScore = async () => {
    if (!scoreForm.nilai_aktual) { toast.error("Nilai aktual wajib diisi"); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.put(`${API_URL}/api/kpi/assignments/${selectedAssignment.id}/score`, {
        nilai_aktual: parseFloat(scoreForm.nilai_aktual),
        catatan: scoreForm.catatan
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Skor berhasil diperbarui");
      setShowScoreDialog(false);
      setScoreForm({ nilai_aktual: "", catatan: "" });
      fetchKpiAssignments();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal update skor"); }
    finally { setLoading(false); }
  };

  const handleCreateReview = async () => {
    if (!reviewForm.employee_id) { toast.error("Karyawan wajib dipilih"); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/performance/review`, {
        ...reviewForm,
        reviewer_id: user.id
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Review berhasil dibuat");
      setShowReviewDialog(false);
      setReviewForm({ employee_id: "", periode: "2026-H1", tipe: "manager" });
      fetchReviews();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal membuat review"); }
    finally { setLoading(false); }
  };

  const getScoreBadge = (skor) => {
    if (!skor) return <Badge variant="secondary">Pending</Badge>;
    if (skor >= 100) return <Badge className="bg-green-500">Excellent ({skor}%)</Badge>;
    if (skor >= 80) return <Badge className="bg-blue-500">Good ({skor}%)</Badge>;
    if (skor >= 60) return <Badge className="bg-yellow-500">Fair ({skor}%)</Badge>;
    return <Badge className="bg-red-500">Poor ({skor}%)</Badge>;
  };

  const getReviewBadge = (skor) => {
    if (!skor) return <Badge variant="secondary">Pending</Badge>;
    if (skor >= 4.5) return <Badge className="bg-green-500">{skor}/5 Excellent</Badge>;
    if (skor >= 3.5) return <Badge className="bg-blue-500">{skor}/5 Good</Badge>;
    if (skor >= 2.5) return <Badge className="bg-yellow-500">{skor}/5 Fair</Badge>;
    return <Badge className="bg-red-500">{skor}/5 Poor</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="performance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Performance Management</h1>
          <p className="text-slate-600">Kelola KPI dan evaluasi kinerja karyawan</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="kpi" data-testid="tab-kpi"><Target className="w-4 h-4 mr-2" />KPI</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review"><Star className="w-4 h-4 mr-2" />Performance Review</TabsTrigger>
        </TabsList>

        {/* KPI Tab */}
        <TabsContent value="kpi" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-4 items-center">
                  <div className="w-32">
                    <Label>Periode</Label>
                    <Select value={selectedPeriode} onValueChange={setSelectedPeriode}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2026-Q1">Q1 2026</SelectItem>
                        <SelectItem value="2026-Q2">Q2 2026</SelectItem>
                        <SelectItem value="2025-Q4">Q4 2025</SelectItem>
                        <SelectItem value="2025-Q3">Q3 2025</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-48">
                    <Label>Karyawan</Label>
                    <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                      <SelectTrigger><SelectValue placeholder="Semua" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua Karyawan</SelectItem>
                        {employees.map((e) => (
                          <SelectItem key={e.id} value={e.id}>{e.nama_lengkap}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={fetchKpiAssignments}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
                  <Button variant="outline" onClick={() => setShowAssignDialog(true)} data-testid="btn-assign-kpi">
                    <Users className="w-4 h-4 mr-2" />Assign KPI
                  </Button>
                  <Button onClick={() => setShowKpiDialog(true)} data-testid="btn-add-kpi">
                    <Plus className="w-4 h-4 mr-2" />Tambah KPI
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KPI Templates */}
          <Card>
            <CardHeader><CardTitle>Template KPI</CardTitle></CardHeader>
            <CardContent>
              {kpis.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Target className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Belum ada template KPI</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {kpis.map((kpi) => (
                    <Card key={kpi.id} className="border">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold">{kpi.nama}</h3>
                            <p className="text-sm text-slate-500">{kpi.deskripsi || "-"}</p>
                          </div>
                          <Badge variant="outline">{kpi.satuan}</Badge>
                        </div>
                        <div className="mt-4 flex justify-between text-sm">
                          <span>Target: <strong>{kpi.target}</strong></span>
                          <span>Bobot: <strong>{kpi.bobot}%</strong></span>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">Dept: {kpi.department_nama}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* KPI Assignments */}
          <Card>
            <CardHeader>
              <CardTitle>KPI Karyawan - {selectedPeriode}</CardTitle>
              <CardDescription>Status pencapaian KPI karyawan</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 animate-spin text-slate-400" /></div>
              ) : kpiAssignments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Belum ada KPI yang di-assign untuk periode ini</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Karyawan</TableHead>
                      <TableHead>KPI</TableHead>
                      <TableHead className="text-right">Target</TableHead>
                      <TableHead className="text-right">Aktual</TableHead>
                      <TableHead>Skor</TableHead>
                      <TableHead>Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kpiAssignments.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell className="font-medium">{a.employee_nama}</TableCell>
                        <TableCell>{a.kpi_nama}</TableCell>
                        <TableCell className="text-right">{a.target}</TableCell>
                        <TableCell className="text-right">{a.nilai_aktual ?? "-"}</TableCell>
                        <TableCell>{getScoreBadge(a.skor)}</TableCell>
                        <TableCell>
                          {a.status === "pending" && (
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedAssignment(a); setShowScoreDialog(true); }}>
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Review Tab */}
        <TabsContent value="review" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-4 items-center">
                  <div className="w-32">
                    <Label>Periode</Label>
                    <Select value={selectedPeriode} onValueChange={setSelectedPeriode}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2026-H1">H1 2026</SelectItem>
                        <SelectItem value="2025-H2">H2 2025</SelectItem>
                        <SelectItem value="2025-H1">H1 2025</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={() => setShowReviewDialog(true)} data-testid="btn-create-review">
                  <Plus className="w-4 h-4 mr-2" />Buat Review
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Performance Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 animate-spin text-slate-400" /></div>
              ) : reviews.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Award className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Belum ada performance review</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Karyawan</TableHead>
                      <TableHead>Reviewer</TableHead>
                      <TableHead>Tipe</TableHead>
                      <TableHead>Periode</TableHead>
                      <TableHead>Skor</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviews.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.employee_nama}</TableCell>
                        <TableCell>{r.reviewer_nama}</TableCell>
                        <TableCell><Badge variant="outline">{r.tipe}</Badge></TableCell>
                        <TableCell>{r.periode}</TableCell>
                        <TableCell>{getReviewBadge(r.skor_rata)}</TableCell>
                        <TableCell>
                          {r.status === "submitted" ? (
                            <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Submitted</Badge>
                          ) : (
                            <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Draft</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add KPI Dialog */}
      <Dialog open={showKpiDialog} onOpenChange={setShowKpiDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tambah Template KPI</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama KPI *</Label><Input value={kpiForm.nama} onChange={(e) => setKpiForm({...kpiForm, nama: e.target.value})} placeholder="Revenue Growth" /></div>
            <div><Label>Deskripsi</Label><Textarea value={kpiForm.deskripsi} onChange={(e) => setKpiForm({...kpiForm, deskripsi: e.target.value})} placeholder="Deskripsi KPI" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Target *</Label><Input type="number" value={kpiForm.target} onChange={(e) => setKpiForm({...kpiForm, target: e.target.value})} placeholder="100" /></div>
              <div><Label>Satuan</Label>
                <Select value={kpiForm.satuan} onValueChange={(v) => setKpiForm({...kpiForm, satuan: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="persen">Persen (%)</SelectItem>
                    <SelectItem value="angka">Angka</SelectItem>
                    <SelectItem value="rupiah">Rupiah</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Bobot (%)</Label><Input type="number" value={kpiForm.bobot} onChange={(e) => setKpiForm({...kpiForm, bobot: e.target.value})} /></div>
              <div><Label>Departemen</Label>
                <Select value={kpiForm.department_id} onValueChange={(v) => setKpiForm({...kpiForm, department_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Semua" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Departemen</SelectItem>
                    {departments.map((d) => (<SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKpiDialog(false)}>Batal</Button>
            <Button onClick={handleCreateKpi} disabled={loading}>{loading ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign KPI Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign KPI ke Karyawan</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Karyawan *</Label>
              <Select value={assignForm.employee_id} onValueChange={(v) => setAssignForm({...assignForm, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.status === 'aktif').map((e) => (<SelectItem key={e.id} value={e.id}>{e.nama_lengkap}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>KPI *</Label>
              <Select value={assignForm.kpi_id} onValueChange={(v) => setAssignForm({...assignForm, kpi_id: v})}>
                <SelectTrigger><SelectValue placeholder="Pilih KPI" /></SelectTrigger>
                <SelectContent>
                  {kpis.map((k) => (<SelectItem key={k.id} value={k.id}>{k.nama} (Target: {k.target})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Periode</Label>
              <Select value={assignForm.periode} onValueChange={(v) => setAssignForm({...assignForm, periode: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026-Q1">Q1 2026</SelectItem>
                  <SelectItem value="2026-Q2">Q2 2026</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Batal</Button>
            <Button onClick={handleAssignKpi} disabled={loading}>{loading ? "Menyimpan..." : "Assign"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Score Dialog */}
      <Dialog open={showScoreDialog} onOpenChange={setShowScoreDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update Skor KPI</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {selectedAssignment && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p><strong>{selectedAssignment.employee_nama}</strong></p>
                <p className="text-sm text-slate-600">KPI: {selectedAssignment.kpi_nama}</p>
                <p className="text-sm text-slate-600">Target: {selectedAssignment.target}</p>
              </div>
            )}
            <div><Label>Nilai Aktual *</Label><Input type="number" value={scoreForm.nilai_aktual} onChange={(e) => setScoreForm({...scoreForm, nilai_aktual: e.target.value})} placeholder="85" /></div>
            <div><Label>Catatan</Label><Textarea value={scoreForm.catatan} onChange={(e) => setScoreForm({...scoreForm, catatan: e.target.value})} placeholder="Catatan pencapaian" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScoreDialog(false)}>Batal</Button>
            <Button onClick={handleUpdateScore} disabled={loading}>{loading ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Buat Performance Review</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Karyawan *</Label>
              <Select value={reviewForm.employee_id} onValueChange={(v) => setReviewForm({...reviewForm, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.status === 'aktif').map((e) => (<SelectItem key={e.id} value={e.id}>{e.nama_lengkap}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Tipe Review</Label>
              <Select value={reviewForm.tipe} onValueChange={(v) => setReviewForm({...reviewForm, tipe: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Self Assessment</SelectItem>
                  <SelectItem value="manager">Manager Review</SelectItem>
                  <SelectItem value="peer">Peer Review</SelectItem>
                  <SelectItem value="360">360° Feedback</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Periode</Label>
              <Select value={reviewForm.periode} onValueChange={(v) => setReviewForm({...reviewForm, periode: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026-H1">H1 2026</SelectItem>
                  <SelectItem value="2025-H2">H2 2025</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>Batal</Button>
            <Button onClick={handleCreateReview} disabled={loading}>{loading ? "Membuat..." : "Buat Review"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerformancePage;
